import config from '../../config/config';
import DefaultApiLimit from '../../config/defaultLimit';
import currentTimeZone from '../../config/timeZone';

import getRequest from './base/get';
import postRequest from './base/post';

interface QueryParameters {
  limit?: number;
  [key: string]: any;
}

interface AccountParameters {
  accountCustomId?: string;
}

interface AddBookMarkInterFace {
  name: string | number;
  entity: string;
  filters: {
    label: string;
    value: string;
    field: string;
  }[];
}

interface GetBookMarkInterFace {
  entity: string;
  accountCustomId: string;
}

export default {
  // Energy Report API
  // station
  /**
   * Fetch stations API
   * @param {any} query - The query parameters
   */
  fetchEnergyReportListStation: (query: QueryParameters = {}) => {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-station-report/power-consumption-report`,
      {
        ...parameters,
      },
    );
  },
  // point
  /**
   * Fetch Point API
   * @param {any} query - The query parameters
   */
  fetchEnergyReportListPoint: (query: QueryParameters = {}) => {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-point-report/power-consumption-report`,
      {
        ...parameters,
      },
    );
  },
  // chart energy consumption trend based on revenue tiers
  /**
   * Fetch Point API
   * @param {any} query - The query parameters
   */
  fetchReportchart: (query: QueryParameters = {}) => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-station-report/time-based-period`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch report API Station
   * @param {any} data - The account parameters
   * @returns {any} A promise that resolves with the list filters
   */
  fetchListStationFilters: (data: AccountParameters = {}) => {
    return getRequest(`${config.reportApiUrl}api/cpo/v1/charging-station-report/list-filters`, {
      ...data,
    });
  },
  /**
   * Fetch report API Point
   * @param {any} data - The account parameters
   * @returns {any} A promise that resolves with the list filters
   */
  fetchListPointFilters: (data: AccountParameters = {}) => {
    return getRequest(`${config.reportApiUrl}api/cpo/v1/charging-point-report/list-filters`, {
      ...data,
    });
  },

  // Revenue Report API
  // station
  /**
   * Fetch stations API
   * @param {any} query - The query parameters
   */
  fetchRevenueReportListStation: (query: QueryParameters = {}) => {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(`${config.reportApiUrl}api/cpo/v1/charging-station-report/revenue-report`, {
      ...parameters,
    });
  },
  // point
  /**
   * Fetch Point API
   * @param {any} query - The query parameters
   */
  fetchRevenueReportListPoint: (query: QueryParameters = {}) => {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(`${config.reportApiUrl}api/cpo/v1/charging-point-report/revenue-report`, {
      ...parameters,
    });
  },

  // Booking Report API
  // station
  /**
   * Fetch stations API
   * @param {any} query - The query parameters
   */
  fetchBookingReportListStation: (query: QueryParameters = {}) => {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-station-report/charging-schedule-report`,
      {
        ...parameters,
      },
    );
  },
  // point
  /**
   * Fetch Point API
   * @param {any} query - The query parameters
   */
  fetchBookingReportListPoint: (query: QueryParameters = {}) => {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-point-report/charging-schedule-report`,
      {
        ...parameters,
      },
    );
  },

  // charger Utilization Report API
  // station
  /**
   * Fetch stations API
   * @param {any} query - The query parameters
   * @returns {any} The request response promise
   */
  fetchChargerReportListStation: (query: QueryParameters = {}) => {
    const parameters = {
      timeZone: currentTimeZone,
      ...query,
      limit: query?.limit || DefaultApiLimit.limit,
    };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-station-report/charger-utilization-report`,
      {
        ...parameters,
      },
    );
  },
  // point
  /**
   * Fetch Point API
   * @param {any} query - The query parameters
   * @returns {any} The request response promise
   */
  fetchChargerReportListPoint: (query: QueryParameters = {}) => {
    const parameters = { timeZone: currentTimeZone, ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-point-report/charger-utilization-report`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch total uptime vs downtime hours (account totals + per-charger breakdown)
   * @param {any} query - The query parameters (accountCustomId, startDate, endDate, timeZone)
   * @returns {any} The request response promise
   */
  fetchChargerUptimeVsDowntimeReport: (query: QueryParameters = {}) => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-station-report/uptime-vs-downtime-report`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch consolidated idle vs in-use seconds across every charger on the account
   * @param {any} query - The query parameters (accountCustomId, startDate, endDate, timeZone)
   * @returns {any} The request response promise
   */
  fetchChargerUtilizationIdleVsInuseReport: (query: QueryParameters = {}) => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-station-report/charger-utilization-idle-vs-inuse-report`,
      {
        ...parameters,
      },
    );
  },

  // Performance  Report API
  // station
  /**
   * Fetch stations API
   * @param {any} query - The query parameters
   */
  fetchPerformanceReportListStation: (query: QueryParameters = {}) => {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-station-report/performance-report`,
      {
        ...parameters,
      },
    );
  },
  // point
  /**
   * Fetch Point API
   * @param {any} query - The query parameters
   */
  fetchPerformanceReportListPoint: (query: QueryParameters = {}) => {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(`${config.reportApiUrl}api/cpo/v1/charging-point-report/performance-report`, {
      ...parameters,
    });
  },

  // maintenance  Report API
  // station
  /**
   * Fetch stations API
   * @param {any} query - The query parameters
   */
  fetchMaintenanceReportListStation: (query: QueryParameters = {}) => {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-station-report/maintenance-schedule-report`,
      {
        ...parameters,
      },
    );
  },
  // point
  /**
   * Fetch Point API
   * @param {any} query - The query parameters
   */
  fetchMaintenanceReportListPoint: (query: QueryParameters = {}) => {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-point-report/maintenance-schedule-report`,
      {
        ...parameters,
      },
    );
  },

  // Alerts  Report API
  // station
  /**
   * Fetch stations API
   * @param {any} query - The query parameters
   */
  fetchAlertsReportListStation: (query: QueryParameters = {}) => {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(`${config.reportApiUrl}api/cpo/v1/charging-station-report/alert-report`, {
      ...parameters,
    });
  },
  // point
  /**
   * Fetch Point API
   * @param {any} query - The query parameters
   */
  fetchAlertsReportListPoint: (query: QueryParameters = {}) => {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(`${config.reportApiUrl}api/cpo/v1/charging-point-report/alert-report`, {
      ...parameters,
    });
  },
  /**
   * Fetch Energic stations API for export
   * @param {any} query - The query parameters
   */
  fetchEnergicStationListExport: (query: QueryParameters = {}) => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-station-report/power-consumption-report-export`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch Revenue stations API for export
   * @param {any} query - The query parameters
   */
  fetchRevenueStationListExport: (query: QueryParameters = {}) => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-station-report/revenue-report-export`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch Booking stations API for export
   * @param {any} query - The query parameters
   */
  fetchBookingStationListExport: (query: QueryParameters = {}) => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-station-report/charging-schedule-report-export`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch Charger stations API for export
   * @param {any} query - The query parameters
   * @returns {any} The request response promise
   */
  fetchChargerStationListExport: (query: QueryParameters = {}) => {
    const parameters = { timeZone: currentTimeZone, ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-station-report/charger-utilization-report-export`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch Performance stations API for export
   * @param {any} query - The query parameters
   */
  fetchPerformanceStationListExport: (query: QueryParameters = {}) => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-station-report/performance-report-export`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch Maintenance stations API for export
   * @param {any} query - The query parameters
   */
  fetchMaintenanceStationListExport: (query: QueryParameters = {}) => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-station-report/maintenance-schedule-report-export`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch Alert stations API for export
   * @param {any} query - The query parameters
   */
  fetchAlertStationListExport: (query: QueryParameters = {}) => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-station-report/alert-report-export`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch Energic points API for export
   * @param {any} query - The query parameters
   */
  fetchEnergicPointListExport: (query: QueryParameters = {}) => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-point-report/power-consumption-report-export`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch Revenue points API for export
   * @param {any} query - The query parameters
   */
  fetchRevenuePointListExport: (query: QueryParameters = {}) => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-point-report/revenue-report-export`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch Booking points API for export
   * @param {any} query - The query parameters
   */
  fetchBookingPointListExport: (query: QueryParameters = {}) => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-point-report/charging-schedule-report-export`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch Charger points API for export
   * @param {any} query - The query parameters
   * @returns {any} The request response promise
   */
  fetchChargerPointListExport: (query: QueryParameters = {}) => {
    const parameters = { timeZone: currentTimeZone, ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-point-report/charger-utilization-report-export`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch Performance points API for export
   * @param {any} query - The query parameters
   */
  fetchPerformancePointListExport: (query: QueryParameters = {}) => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-point-report/performance-report-export`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch Maintenance points API for export
   * @param {any} query - The query parameters
   */
  fetchMaintenancePointListExport: (query: QueryParameters = {}) => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-point-report/maintenance-schedule-report-export`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch Alert points API for export
   * @param {any} query - The query parameters
   */
  fetchAlertPointListExport: (query: QueryParameters = {}) => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-point-report/alert-report-export`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch stations API for export
   * @param {any} query - The query parameters
   */
  fetchReportListExport: (query: QueryParameters = {}) => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-transaction/list-power-consumed/export-data`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch the report list with pagination
   * @param {any} query - The query
   */
  fetchReports: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-transaction/station-based-reports`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch the Report list with pagination
   */
  fetchReportListFilters: (): Promise<any> => {
    return getRequest(`${config.reportApiUrl}api/cpo/v1/charging-transaction/list-filters`);
  },
  /**
   * Fetch the report details
   */
  fetchReportDetails: (): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-transaction/charging-transaction-revenue-details`,
      {},
    );
  },
  /**
   * export the report list
   * @param {any} query - The query
   */
  exportReports: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-transaction/station-based-reports/export`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Add the book mark
   * @param {any} query - The query
   */
  addBookMark: (query: AddBookMarkInterFace): Promise<any> => {
    const parameters = { ...query };
    return postRequest(`${config.subscription.accountApiUrl}api/cpo/v1/filter-bookmark`, {
      ...parameters,
    });
  },
  /**
   * Get the book marks
   * @param {any} query - The query
   */
  getBookMarks: (query: GetBookMarkInterFace): Promise<any> => {
    const parameters = { ...query };
    return getRequest(`${config.subscription.accountApiUrl}api/cpo/v1/filter-bookmark`, {
      ...parameters,
    });
  },

  /**
   * Fetch Driver Revenue Reports (Mobility)
   * @param {QueryParameters} [query] - Query parameters
   * @param {string} [userCustomId] - Optional driver custom ID
   * @returns {Promise<any>} Response promise
   */
  fetchDriverRevenueReports: (query: QueryParameters = {}, userCustomId?: string): Promise<any> => {
    const endpoint = userCustomId
      ? `${config.reportApiUrl}api/super-admin/v1/end-consumer/driver-revenue-reports/${userCustomId}`
      : `${config.reportApiUrl}api/super-admin/v1/end-consumer/driver-revenue-reports`;
    return getRequest(endpoint, query);
  },

  /**
   * Fetch Driver Power/Energy Consumed Reports (Mobility)
   * @param {QueryParameters} [query] - Query parameters
   * @param {string} [userCustomId] - Optional driver custom ID
   * @returns {Promise<any>} Response promise
   */
  fetchDriverEnergyReports: (query: QueryParameters = {}, userCustomId?: string): Promise<any> => {
    const endpoint = userCustomId
      ? `${config.reportApiUrl}api/super-admin/v1/end-consumer/driver-power-consumed-reports/${userCustomId}`
      : `${config.reportApiUrl}api/super-admin/v1/end-consumer/driver-power-consumed-reports`;
    return getRequest(endpoint, query);
  },
};
